import { LocalizedText, LocalizedElement, sourceAttribute, useSourceLanguage } from "../../i18n/LocalizedCopy";
import React, { forwardRef } from "react";
import { SUBSCRIPTION_TRANSLATIONS } from "../../i18n/subscriptionTranslations";

export function formatWelcomeOfferAmount(amountMinor, currency, locale = "fr-FR") {
  if (!Number.isSafeInteger(amountMinor) || !currency) return "";
  try {
    const formatter = new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
    });
    const fractionDigits = formatter.resolvedOptions().maximumFractionDigits;
    return formatter.format(amountMinor / 10 ** fractionDigits);
  } catch (_error) {
    return "";
  }
}

function MarkerIcon({ type }) {
  useSourceLanguage();
  if (type === "shield") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 3.5 19 6v5.2c0 4.2-2.5 7.3-7 9.3-4.5-2-7-5.1-7-9.3V6l7-2.5Z" />
        <path d="m9 12 2 2 4-4" />
      </svg>
    );
  }
  if (type === "spark") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="m13 2-6.5 11H12l-1 9 6.5-11H12l1-9Z" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="8.5" />
      <path d="m8.5 12 2.25 2.25L15.5 9.5" />
    </svg>
  );
}

const WelcomeOfferDetails = forwardRef(function WelcomeOfferDetails(
  { "data-et-copy": __etCopy = "", offer, phase, message },
  titleRef
) {
  useSourceLanguage();
  const amount = formatWelcomeOfferAmount(offer?.amountMinor, offer?.currency);
  const isWelcome10 =
    offer?.offerCode === "WELCOME10" &&
    offer?.amountMinor === 1000 &&
    offer?.currency === "EUR";
  const hasIssue = ["recoverable", "offline", "uncertain", "unavailable"].includes(
    phase
  );

  return (
    <div className="welcome-offer-details">
      <p className="welcome-offer-details__eyebrow"><LocalizedText source={"BIENVENUE DANS ETERNUA"} /></p>
      <h2 id="welcome-offer-title" ref={titleRef} tabIndex="-1"><LocalizedText source={"Ton univers commence ici"} /></h2>
      <p id="welcome-offer-description" className="welcome-offer-details__subtitle"><LocalizedText source={"Ton bonus de bienvenue est prêt"} /></p>

      <div className="welcome-offer-details__code" aria-label={`Code de l’offre ${offer.offerCode}`}>
        <span aria-hidden="true">✦</span>
        <strong>{offer.offerCode}</strong>
        <span aria-hidden="true">✦</span>
      </div>

      <div className="welcome-offer-details__amount-card">
        <p className={isWelcome10 ? "welcome-offer-details__promotion-label" : undefined}>
          <strong>
            {isWelcome10
              ? <LocalizedText source={SUBSCRIPTION_TRANSLATIONS["subscriptions.promo.welcome10"]} />
              : amount}
          </strong>
          {!isWelcome10 ? <span><LocalizedText source={"DE BONUS"} /></span> : null}
        </p>
        <div>
          <span className="welcome-offer-details__wallet-icon" aria-hidden="true">▱</span>
          <span><LocalizedText source={"Activé une seule fois et ajouté à ton portefeuille Eternua."} /></span>
        </div>
      </div>

      <LocalizedElement elementType={"ul"} copyAttributes={["aria-label"]} className="welcome-offer-details__markers" aria-label={sourceAttribute("Caractéristiques de l’offre")}>
        <li><MarkerIcon type="shield" /><span><LocalizedText source={"Sécurisé"} /></span></li>
        <li><MarkerIcon type="spark" /><span>Confirmation serveur</span></li>
        <li><MarkerIcon type="check" /><span><LocalizedText source={"Une seule activation"} /></span></li>
      </LocalizedElement>

      {hasIssue && message ? (
        <div className={`welcome-offer-details__message welcome-offer-details__message--${phase}`}>
          <span aria-hidden="true">!</span>
          <p>{<LocalizedText source={message} marked={__etCopy.split(',').includes("message")} />}</p>
        </div>
      ) : null}
    </div>
  );
});

export default WelcomeOfferDetails;
